import _, { isNil } from 'lodash'
import AccountGroupIcon from 'mdi-react/AccountGroupIcon'
import CalendarIcon from 'mdi-react/CalendarIcon'
import CalendarQuestionIcon from 'mdi-react/CalendarQuestionIcon'
import CashMultipleIcon from 'mdi-react/CashMultipleIcon'
import ExpandMoreIcon from 'mdi-react/ExpandMoreIcon'
import React, { useCallback, useEffect, useState } from 'react'
import { OrganizationProperties } from 'scenes/Dashboard/components/OrganizationProperties'
import { Organization } from 'scenes/Dashboard/data/organizations/types'

/* eslint-disable no-console */
import { Accordion, AccordionSummary, createStyles, Theme, Typography } from '@mui/material'
import { ClassNameMap, withStyles, WithStyles } from '@mui/styles'

import { getPerformanceIndicatorLabel } from '../../../../../../data/performanceIndicators/helpers'
import { PerformanceIndicator, PerformanceIndicatorType } from '../../../../../../data/performanceIndicators/types'
import FundingRoundsTable from '../FundingRoundsTable'

export function useExpansionHook(defaultExpanded?: boolean) {
  const [expanded, setExpanded] = useState<boolean | undefined>(defaultExpanded)
  useEffect(() => setExpanded(defaultExpanded), [defaultExpanded])
  const toggleExpansion = useCallback(() => setExpanded(!expanded), [expanded])
  return { expanded, toggleExpansion }
}
function BasicOrganizationInfo(props: BasicOrganizationInfoProps & WithStyles<typeof basicOrganizationInfoStyles>) {
  const { classes, organization, blurb, foundedOn, append } = props
  const { expanded, toggleExpansion } = useExpansionHook(false)

  if (nothingToDisplay(props)) {
    return null
  }

  return (
    <>
      {displayBlurb(blurb, classes.blurbContainer)}
      <div className={classes.propertyIconList}>
        {organization && <OrganizationProperties organization={organization} />}
        {displayFoundedOn({ classes, foundedOn })}
        {getEmployeesRanges(props)}
        {displayLastFundingEvent(props)}
        {displayCurrentFundingRound(props)}
        {displayTotalFunding(props)}
      </div>
      <div className={classes.fundingRoundsTable}>
        <Accordion elevation={0} className={classes.Accordion} expanded={expanded} onChange={toggleExpansion}>
          <AccordionSummary
            className={classes.AccordionSummary}
            classes={{ content: classes.AccordionSummary, expandIconWrapper: classes.expandIcon }}
            expandIcon={<ExpandMoreIcon />}
            aria-controls="deal-card-details"
          >
            <Typography align="center" variant="subtitle1">
              Funding events
              <hr className={classes.divider} />
            </Typography>
          </AccordionSummary>
          <FundingRoundsTable organization={organization} />
        </Accordion>
      </div>
      {append}
    </>
  )
}

function nothingToDisplay({
  operatingLocations,
  headquarters,
  industries,
  stage,
  blurb,
  foundedOn,
  announcedDateOfCurrentStageFundingRound,
  urls,
}: BasicOrganizationInfoProps) {
  return (
    !operatingLocations &&
    !headquarters &&
    !industries &&
    !stage &&
    !blurb &&
    !foundedOn &&
    !announcedDateOfCurrentStageFundingRound &&
    (!urls || _.isEmpty(urls))
  )
}

function displayCurrentFundingRound({
  classes,
  currentFundingRound,
}: BasicOrganizationInfoProps & WithStyles<typeof basicOrganizationInfoStyles>) {
  if (!currentFundingRound || !currentFundingRound.currentlyRaising) {
    return null
  }
  const { currentlyRaising, valuationPreMoney, roundTerms, targetMoney, committedInvestors } = currentFundingRound
  return (
    <div className={classes.dataPointContainer} style={{ alignItems: 'flex-start' }}>
      <CashMultipleIcon className={classes.dataPointIcon} />
      <div className={classes.currentFundingRoundContainer}>
        <Typography variant="body2">{currentlyRaising}</Typography>
        {valuationPreMoney && <Typography variant="caption">{valuationPreMoney}</Typography>}
        {roundTerms && <Typography variant="caption">{roundTerms}</Typography>}
        {committedInvestors && <Typography variant="caption">{committedInvestors}</Typography>}
        {targetMoney && <Typography variant="caption">{targetMoney}</Typography>}
      </div>
    </div>
  )
}

function displayLastFundingEvent({
  classes,
  lastFundingRound,
  lastFundingEvent,
}: BasicOrganizationInfoProps & WithStyles<typeof basicOrganizationInfoStyles>) {
  if (!lastFundingRound && !lastFundingEvent) {
    return null
  }

  const text = []
  if (lastFundingRound?.roundType) {
    text.push(lastFundingRound.roundType)
  }
  if (lastFundingRound?.roundSize && lastFundingRound?.roundSize !== '0') {
    text.push(lastFundingRound.roundSize)
  }
  if (lastFundingRound?.announcedOn) {
    text.push(
      new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' }).format(
        new Date(lastFundingRound.announcedOn)
      )
    )
  }

  if (!lastFundingEvent && !text.length) {
    return null
  }

  return (
    <div className={classes.lastFundingRoundContainer} style={{ alignItems: 'flex-start' }}>
      <CalendarQuestionIcon className={classes.dataPointIcon} />
      <div className={classes.currentFundingRoundContainer}>
        <Typography variant="body2">Last funding event: {lastFundingEvent ?? text.join(', ')}</Typography>
      </div>
    </div>
  )
}

function displayTotalFunding({
  classes,
  totalFunding,
}: BasicOrganizationInfoProps & WithStyles<typeof basicOrganizationInfoStyles>) {
  if (isNil(totalFunding) || !parseInt(totalFunding)) {
    return null
  }

  return (
    <div className={classes.totalFundingContainer} style={{ alignItems: 'flex-start' }}>
      <CashMultipleIcon className={classes.dataPointIcon} />
      <div className={classes.currentFundingRoundContainer}>
        <Typography variant="body2">
          Total funding: {totalFunding} {}
        </Typography>
      </div>
    </div>
  )
}

const displayBlurb = (blurb: string | undefined, containerClassName?: string) => {
  if (!blurb) {
    return null
  }
  return (
    <div className={containerClassName}>
      <Typography variant="body2">{blurb}</Typography>
    </div>
  )
}

const displayFoundedOn = ({
  classes,
  foundedOn,
}: { foundedOn: Date | undefined } & WithStyles<typeof basicOrganizationInfoStyles>) => {
  if (!_.isDate(foundedOn)) {
    return null
  }
  return (
    <div className={classes.totalFundingContainer} style={{ alignItems: 'flex-start' }}>
      <CalendarIcon className={classes.dataPointIcon} />
      <div className={classes.currentFundingRoundContainer}>
        <Typography variant="body2">
          <span>Founded on: </span>
          {foundedOn.getUTCFullYear()}
        </Typography>
      </div>
    </div>
  )
}

const getEmployeesRanges = ({
  classes,
  organization,
}: BasicOrganizationInfoProps & WithStyles<typeof basicOrganizationInfoStyles>) => {
  if (!organization) {
    return []
  }
  const performanceIndicatorsMap = new Map<PerformanceIndicatorType, PerformanceIndicator>()
  organization?.performanceIndicators
    ?.filter((pi) =>
      [
        PerformanceIndicatorType.linkedinEmployees,
        PerformanceIndicatorType.linkedinRangeEmployees,
        PerformanceIndicatorType.crunchbaseRangeEmployees,
      ].includes(pi.type)
    )
    .sort(function (b, a) {
      return new Date(b.measuredOn).getTime() - new Date(a.measuredOn).getTime()
    })
    .forEach((pi) => {
      performanceIndicatorsMap.set(pi.type, pi)
    })

  if (performanceIndicatorsMap.size) {
    return Array.from(performanceIndicatorsMap.values()).map((pi) => getRangeEmployeesText(pi, classes))
  }

  return []
}

const getRangeEmployeesText = (pi: PerformanceIndicator, classes: ClassNameMap<string>) => {
  const textValue = getRangeEmployeesTextValue(pi?.value)
  return (
    <div className={classes.dataPointContainer} style={{ alignItems: 'flex-start' }}>
      <AccountGroupIcon className={classes.dataPointIcon} />
      <div className={classes.currentFundingRoundContainer}>
        <Typography variant="body2">
          {textValue !== undefined ? `${getPerformanceIndicatorLabel(pi.type)}: ${textValue}` : undefined}
        </Typography>
      </div>
    </div>
  )
}
const getRangeEmployeesTextValue = (value: PerformanceIndicator['value']) => {
  if (typeof value === 'number' || typeof value === 'string') {
    return value.toString()
  } else if (!!value) {
    return value.max !== undefined ? `${value.min}-${value.max}` : `+${value.min}`
  } else {
    return undefined
  }
}
export type BasicOrganizationInfoProps = {
  children?: React.ReactNode
  organization?: Organization
  headquarters?: string
  operatingLocations?: string
  industries?: string
  types?: string
  stage?: string
  totalFunding?: string
  lastFundingRound?: {
    roundType?: string
    roundSize?: string
    announcedOn?: Date | string
  }
  lastFundingEvent?: string
  currentFundingRound?: {
    currentlyRaising: string
    valuationPreMoney?: string
    roundTerms?: string
    targetMoney?: string
    committedInvestors?: string
  }
  blurb?: string
  foundedOn?: Date
  announcedDateOfCurrentStageFundingRound?: Date | string
  urls?: {
    linkedin?: string
    facebook?: string
    twitter?: string
    website?: string
  }
  append?: React.ReactNode
}

export const basicOrganizationInfoStyles = ({ spacing, palette }: Theme) => {
  const iconStyle = {
    fill: palette.primary.main,
    flex: '0 0 auto',
  }

  const dataPointContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    marginBottom: spacing(0.5),
  }

  return createStyles({
    dataPointContainer: {
      ...dataPointContainerStyle,
    },
    lastFundingRoundContainer: {
      ...dataPointContainerStyle,
    },
    totalFundingContainer: {
      ...dataPointContainerStyle,
    },
    foundedOnAndUrlsContainer: {
      ...dataPointContainerStyle,
      marginTop: spacing(2),
    },
    currentFundingRoundContainer: {
      display: 'flex',
      flexDirection: 'column',
    },
    icon: {
      ...iconStyle,
    },
    dataPointIcon: {
      ...iconStyle,
      width: spacing(2),
      height: spacing(2),
      marginRight: spacing(),
    },
    iconContainer: {
      display: 'inline-flex',
    },
    blurbContainer: {
      whiteSpace: 'pre-line',
      marginBottom: spacing(2),
    },
    fundingRoundsTable: {
      position: 'relative',
      borderRadius: '10px',
      border: '2px solid rgba(0,0,0,0.1)',
      padding: '4px',
    },
    divider: {
      border: '0px',
    },
    Accordion: {
      background: 'transparent',
      cursor: 'auto',
      userSelect: 'auto',
    },
    AccordionSummary: {
      cursor: 'auto',
      userSelect: 'auto',
      textAlign: 'center',
      paddingLeft: 4,
      paddingRight: 4,
    },
    expandIcon: {
      position: 'absolute !important',
      borderRadius: 10,
      bottom: 10,
      background: '#bdbdbd',
      color: 'white',
      width: '98%',
      justifyContent: 'center',
      transform: 'rotate(180) !important',
      transition: 'all',
    },
    propertyIconList: {
      marginBottom: spacing(2),
    },
  })
}
export default withStyles(basicOrganizationInfoStyles)(BasicOrganizationInfo)
